import { cn } from '@/lib/utils';

export interface ChartTooltipRow {
  label: string;
  value: string;
  /** Series colour, rendered as a short stroke. Omit for a single-series chart. */
  color?: string;
  /** Secondary line under the value — e.g. "of $250.00 budget". */
  note?: string;
}

export interface ChartTooltipProps {
  title: string;
  rows: ChartTooltipRow[];
}

/**
 * One tooltip for every chart in the Analysis view.
 *
 * Hierarchy is inverted relative to a legend on purpose: the reader already knows which series they
 * are pointing at, so the VALUE is the high-contrast element and the label is secondary. A legend
 * has the opposite job and the opposite emphasis.
 *
 * Series are keyed with a 2px stroke rather than a filled swatch — at tooltip density a solid block
 * is data-weight ink doing a label's job.
 *
 * Everything here renders through JSX text children, so React escapes it. That matters because
 * these labels are service slugs and email addresses that arrive from the API, i.e. untrusted.
 */
export function ChartTooltip({ title, rows }: ChartTooltipProps) {
  return (
    <div
      className={cn(
        'pointer-events-none min-w-[150px] max-w-[260px] rounded-md border border-border/80 bg-popover px-2.5 py-2 shadow-md',
      )}
    >
      <p className="oa-micro mb-1.5 truncate">{title}</p>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-center gap-1.5">
                {row.color && (
                  <span
                    aria-hidden
                    className="h-0.5 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: row.color }}
                  />
                )}
                <span className="truncate text-[11px] text-muted-foreground">{row.label}</span>
              </span>
              <span className="shrink-0 text-[12px] font-semibold tabular-nums text-foreground">
                {row.value}
              </span>
            </div>
            {row.note && <p className="mt-0.5 text-[10px] text-muted-foreground">{row.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
